import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import {
  Plus,
  Star,
  Trash2,
  TrendingUp,
  TrendingDown,
  Edit2,
  MoreVertical,
  Search,
  Loader2
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { watchlistsAPI, stocksAPI } from '../services/api';
import { markets, formatCurrency } from '../data/mockData';

const WatchlistPage = ({ user }) => {
  const navigate = useNavigate();
  const [watchlists, setWatchlists] = useState([]);
  const [selectedWatchlist, setSelectedWatchlist] = useState(null);
  const [watchlistStocks, setWatchlistStocks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stocksLoading, setStocksLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [isCreating, setIsCreating] = useState(false);
  const [newWatchlistName, setNewWatchlistName] = useState('');

  // Fetch watchlists on mount
  useEffect(() => {
    fetchWatchlists();
  }, []);

  // Fetch stocks when watchlist changes
  useEffect(() => {
    if (selectedWatchlist?.stocks?.length > 0) {
      fetchWatchlistStocks();
    } else {
      setWatchlistStocks([]);
    }
  }, [selectedWatchlist]);

  const fetchWatchlists = async () => {
    try {
      setLoading(true);
      const data = await watchlistsAPI.getWatchlists();
      // Ensure data is an array
      const watchlistsArray = Array.isArray(data) ? data : (data?.watchlists || []);
      setWatchlists(watchlistsArray);
      if (watchlistsArray.length > 0 && !selectedWatchlist) {
        setSelectedWatchlist(watchlistsArray[0]);
      }
    } catch (error) {
      console.error('Error fetching watchlists:', error);
      setWatchlists([]); // Set empty array on error
    } finally {
      setLoading(false);
    }
  };

  const fetchWatchlistStocks = async () => {
    if (!selectedWatchlist?.stocks?.length) return;
    
    setStocksLoading(true);
    try {
      // Fetch each stock's data
      const stockPromises = selectedWatchlist.stocks.map(async (ref) => {
        try {
          return await stocksAPI.getStock(ref.market, ref.symbol);
        } catch {
          return { ...ref, name: ref.symbol, price: 0, change: 0, pe: 0, roe: 0 };
        }
      });
      const stocks = await Promise.all(stockPromises);
      setWatchlistStocks(stocks);
    } catch (error) {
      console.error('Error fetching stocks:', error);
    } finally {
      setStocksLoading(false);
    }
  };

  const getMarketFlag = (code) => {
    const market = markets.find(m => m.code === code);
    return market?.flag || '';
  };

  const handleCreateWatchlist = async () => {
    if (newWatchlistName.trim()) {
      try {
        const newWatchlist = await watchlistsAPI.createWatchlist({
          name: newWatchlistName,
          description: ''
        });
        setWatchlists([...watchlists, newWatchlist]);
        setSelectedWatchlist(newWatchlist);
        setNewWatchlistName('');
        setIsCreating(false);
      } catch (error) {
        console.error('Error creating watchlist:', error);
      }
    }
  };

  const handleDeleteWatchlist = async (id) => {
    try {
      await watchlistsAPI.deleteWatchlist(id);
      const filtered = watchlists.filter(w => w.watchlist_id !== id);
      setWatchlists(filtered);
      if (selectedWatchlist?.watchlist_id === id) {
        setSelectedWatchlist(filtered[0] || null);
      }
    } catch (error) {
      console.error('Error deleting watchlist:', error);
    }
  };

  const handleRemoveStock = async (market, symbol) => {
    if (!selectedWatchlist) return;
    try {
      await watchlistsAPI.removeStock(selectedWatchlist.watchlist_id, market, symbol);
      // Update local state
      const updatedStocks = selectedWatchlist.stocks.filter(
        s => !(s.market === market && s.symbol === symbol)
      );
      const updated = { ...selectedWatchlist, stocks: updatedStocks };
      setSelectedWatchlist(updated);
      setWatchlists(watchlists.map(w => w.watchlist_id === updated.watchlist_id ? updated : w));
      setWatchlistStocks(watchlistStocks.filter(
        s => !(s.market === market && s.symbol === symbol)
      ));
    } catch (error) {
      console.error('Error removing stock:', error);
    }
  };

  const filteredStocks = watchlistStocks.filter(stock => 
    !searchQuery || 
    stock.symbol?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    stock.name?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (loading) {
    return (
      <div className="pt-24 pb-12 px-4 sm:px-6 lg:px-8 flex items-center justify-center min-h-[60vh]">
        <Loader2 className="w-8 h-8 text-emerald-400 animate-spin" />
      </div>
    );
  }

  return (
    <div className="pt-20 pb-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-white mb-2">
              Mes Watchlists
            </h1>
            <p className="text-gray-400">
              Suivez vos opportunités d'investissement
            </p>
          </div>
          <Button 
            onClick={() => setIsCreating(true)}
            className="bg-emerald-500 hover:bg-emerald-600 text-white"
          >
            <Plus className="w-4 h-4 mr-2" />
            Nouvelle watchlist
          </Button>
        </div>

        <div className="grid lg:grid-cols-4 gap-6">
          {/* Watchlists Sidebar */}
          <div className="lg:col-span-1 space-y-4">
            {/* Create New Watchlist Card */}
            {isCreating && (
              <Card className="bg-white/5 border-emerald-500/30">
                <CardContent className="p-4">
                  <Input
                    type="text"
                    placeholder="Nom de la watchlist"
                    value={newWatchlistName}
                    onChange={(e) => setNewWatchlistName(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleCreateWatchlist()}
                    className="bg-white/5 border-white/10 text-white placeholder:text-gray-500 mb-3"
                    autoFocus
                  />
                  <div className="flex gap-2">
                    <Button 
                      size="sm" 
                      onClick={handleCreateWatchlist}
                      className="bg-emerald-500 hover:bg-emerald-600 text-white flex-1"
                    >
                      Créer
                    </Button>
                    <Button 
                      size="sm" 
                      variant="ghost"
                      onClick={() => { setIsCreating(false); setNewWatchlistName(''); }}
                      className="text-gray-400 hover:text-white"
                    >
                      Annuler
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Watchlist Cards */}
            {watchlists.map((watchlist) => (
              <Card 
                key={watchlist.watchlist_id}
                className={`bg-white/5 border-white/10 cursor-pointer transition-all ${
                  selectedWatchlist?.watchlist_id === watchlist.watchlist_id 
                    ? 'ring-1 ring-emerald-500 border-emerald-500/30' 
                    : 'hover:border-white/20'
                }`}
                onClick={() => setSelectedWatchlist(watchlist)}
              >
                <CardContent className="p-4">
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <Star className={`w-4 h-4 ${
                        selectedWatchlist?.watchlist_id === watchlist.watchlist_id 
                          ? 'fill-emerald-400 text-emerald-400' 
                          : 'text-gray-500'
                      }`} />
                      <h3 className="font-medium text-white">{watchlist.name}</h3>
                    </div>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="sm" className="h-8 w-8 p-0 text-gray-400 hover:text-white">
                          <MoreVertical className="w-4 h-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem className="text-gray-300">
                          <Edit2 className="w-4 h-4 mr-2" />
                          Renommer
                        </DropdownMenuItem>
                        <DropdownMenuItem 
                          className="text-red-400"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteWatchlist(watchlist.watchlist_id);
                          }}
                        >
                          <Trash2 className="w-4 h-4 mr-2" />
                          Supprimer
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                  <p className="text-sm text-gray-500 mb-2 line-clamp-2">
                    {watchlist.description || 'Aucune description'}
                  </p>
                  <div className="flex items-center gap-2">
                    <Badge variant="outline" className="border-white/20 text-gray-400 text-xs">
                      {watchlist.stocks?.length || 0} actions
                    </Badge>
                  </div>
                </CardContent>
              </Card>
            ))}

            {watchlists.length === 0 && !isCreating && (
              <Card className="bg-white/5 border-white/10">
                <CardContent className="p-6 text-center">
                  <Star className="w-10 h-10 text-gray-600 mx-auto mb-3" />
                  <p className="text-gray-400 mb-3">Aucune watchlist</p>
                  <Button 
                    size="sm"
                    onClick={() => setIsCreating(true)}
                    className="bg-emerald-500 hover:bg-emerald-600 text-white"
                  >
                    Créer ma première watchlist
                  </Button>
                </CardContent>
              </Card>
            )}
          </div>

          {/* Watchlist Content */}
          <div className="lg:col-span-3">
            {selectedWatchlist ? (
              <Card className="bg-white/5 border-white/10">
                <CardHeader className="border-b border-white/10">
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                    <div>
                      <CardTitle className="text-white text-xl flex items-center gap-2">
                        <Star className="w-5 h-5 fill-emerald-400 text-emerald-400" />
                        {selectedWatchlist.name}
                      </CardTitle>
                      {selectedWatchlist.description && (
                        <p className="text-sm text-gray-400 mt-1">{selectedWatchlist.description}</p>
                      )}
                    </div>
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                      <Input
                        type="text"
                        placeholder="Rechercher..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="pl-9 w-full sm:w-64 bg-white/5 border-white/10 text-white placeholder:text-gray-500"
                      />
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="p-0">
                  {stocksLoading ? (
                    <div className="p-12 flex items-center justify-center">
                      <Loader2 className="w-6 h-6 text-emerald-400 animate-spin" />
                    </div>
                  ) : filteredStocks.length > 0 ? (
                    <div className="divide-y divide-white/5">
                      {filteredStocks.map((stock) => (
                        <div 
                          key={`${stock.market}-${stock.symbol}`}
                          className="p-4 hover:bg-white/5 transition-colors flex items-center justify-between gap-4"
                        >
                          <Link 
                            to={`/stock/${stock.market}-${stock.symbol}`}
                            className="flex-1 min-w-0"
                          >
                            <div className="flex items-center gap-4">
                              <div>
                                <div className="flex items-center gap-2">
                                  <span className="font-medium text-white hover:text-emerald-400 transition-colors">
                                    {stock.symbol}
                                  </span>
                                  <Badge variant="outline" className="border-white/20 text-gray-400 text-xs">
                                    {getMarketFlag(stock.market)} {stock.market}
                                  </Badge>
                                </div>
                                <div className="text-sm text-gray-500 truncate">{stock.name}</div>
                              </div>
                            </div>
                          </Link>

                          <div className="flex items-center gap-6">
                            <div className="text-right">
                              <div className="font-medium text-white">
                                {formatCurrency(stock.price, stock.currency)}
                              </div>
                              <div className={`text-sm flex items-center justify-end gap-1 ${
                                stock.change == null ? 'text-gray-500' :
                                stock.change >= 0 ? 'text-emerald-400' : 'text-red-400'
                              }`}>
                                {stock.change == null ? null : stock.change >= 0 ? (
                                  <TrendingUp className="w-3 h-3" />
                                ) : (
                                  <TrendingDown className="w-3 h-3" />
                                )}
                                {stock.change == null ? '—' : `${stock.change >= 0 ? '+' : ''}${stock.change.toFixed(1)}%`}
                              </div>
                            </div>

                            <div className="hidden sm:flex items-center gap-4 text-sm text-gray-400">
                              <div>
                                <span className="text-gray-500">P/E</span>
                                <span className="ml-1 text-white">{stock.pe == null ? '—' : stock.pe.toFixed(1)}</span>
                              </div>
                              <div>
                                <span className="text-gray-500">ROE</span>
                                <span className="ml-1 text-emerald-400">{stock.roe == null ? '—' : `${stock.roe.toFixed(1)}%`}</span>
                              </div>
                            </div>

                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleRemoveStock(stock.market, stock.symbol)}
                              className="text-gray-500 hover:text-red-400 hover:bg-red-500/10"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="p-12 text-center">
                      <Star className="w-12 h-12 text-gray-600 mx-auto mb-4" />
                      <h3 className="text-lg font-medium text-white mb-2">
                        {searchQuery ? 'Aucun résultat' : 'Watchlist vide'}
                      </h3>
                      <p className="text-gray-400 mb-4">
                        {searchQuery 
                          ? 'Aucune action ne correspond à votre recherche.'
                          : 'Ajoutez des actions depuis le screener pour commencer.'
                        }
                      </p>
                      {!searchQuery && (
                        <Link to="/screener">
                          <Button className="bg-emerald-500 hover:bg-emerald-600 text-white">
                            Aller au screener
                          </Button>
                        </Link>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>
            ) : (
              <Card className="bg-white/5 border-white/10">
                <CardContent className="p-12 text-center">
                  <Star className="w-12 h-12 text-gray-600 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-white mb-2">
                    Sélectionnez une watchlist
                  </h3>
                  <p className="text-gray-400">
                    Choisissez une watchlist dans la barre latérale ou créez-en une nouvelle.
                  </p>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default WatchlistPage;
